/**
 * Helpers shared by the IRL-tab folder picker and the matchers (VDR + Folder
 * Mirroring). Two responsibilities:
 *
 *   1. Build a tree of folder codes from the project's parsed `IndexRow[]`,
 *      so the picker can render a Reddit-style expandable hierarchy.
 *   2. Resolve a Folder-Mirroring relative path (e.g., "Financials/Q4") to
 *      the closest matching code, so the same exclusion list works for
 *      both upload modes.
 *
 * The exclusion check `isCodeExcluded(code, excludedSet)` walks ancestors —
 * excluding "1.3" automatically excludes every "1.3.x" descendant, and
 * excluding "1" hides the entire section. The set stores user intent at
 * whatever depth they clicked; ancestor-walking does the implicit fan-out.
 */

import type { IndexRow } from "../../vdr/providers";

export type FolderNode = {
  code: string; // canonical code, e.g., "1", "1.2", "1.2.3"
  title: string;
  depth: number; // 1 for top-level codes ("1"), 2 for "1.2", etc.
  children: FolderNode[];
};

/* ─────────────────────────── Code helpers ─────────────────────────── */

function codeDepth(code: string): number {
  return code.split(".").length;
}

function parentCode(code: string): string | null {
  const i = code.lastIndexOf(".");
  return i === -1 ? null : code.slice(0, i);
}

/**
 * Every ancestor of `code`, plus `code` itself. For "1.2.3" that's
 * ["1", "1.2", "1.2.3"]. Used by the exclusion filter.
 */
export function codeWithAncestors(code: string): string[] {
  const segs = code.split(".");
  const out: string[] = [];
  for (let i = 1; i <= segs.length; i++) {
    out.push(segs.slice(0, i).join("."));
  }
  return out;
}

/**
 * Returns true if any ancestor of `code` (inclusive) is in the excluded
 * set. Null/empty code → not excluded (we don't filter root files).
 */
export function isCodeExcluded(
  code: string | null | undefined,
  excluded: Set<string>,
): boolean {
  if (!code || excluded.size === 0) return false;
  for (const c of codeWithAncestors(code)) {
    if (excluded.has(c)) return true;
  }
  return false;
}

/* ─────────────────────────── Tree builder ─────────────────────────── */

/**
 * Build a tree of folders from `indexRows`, optionally augmented with raw
 * codes from `extraCodes` (e.g., the keys of a saved indexMap). The extra
 * source matters when a project was set up via SharePoint scan rather than
 * an uploaded index file — indexRows is empty in that case but indexMap
 * has every code, just without titles.
 *
 * Intermediate codes implied by deeper entries are synthesized too — if
 * "1.2.3" exists but "1.2" doesn't, we still create a "1.2" node so the
 * tree is well-formed.
 */
export function buildFolderTree(indexRows: IndexRow[], extraCodes?: Iterable<string>): FolderNode[] {
  const titleByCode = new Map<string, string>();
  const allCodes = new Set<string>();
  for (const row of indexRows) {
    if (!row.code) continue;
    titleByCode.set(row.code, row.title || "");
    // Walk up: every ancestor implicitly exists too.
    for (const c of codeWithAncestors(row.code)) {
      allCodes.add(c);
      if (!titleByCode.has(c)) titleByCode.set(c, "");
    }
  }
  if (extraCodes) {
    for (const code of extraCodes) {
      if (!code) continue;
      for (const c of codeWithAncestors(code)) {
        allCodes.add(c);
        if (!titleByCode.has(c)) titleByCode.set(c, "");
      }
    }
  }
  if (allCodes.size === 0) return [];

  const nodeByCode = new Map<string, FolderNode>();
  for (const code of allCodes) {
    nodeByCode.set(code, {
      code,
      title: titleByCode.get(code) || "",
      depth: codeDepth(code),
      children: [],
    });
  }

  const roots: FolderNode[] = [];
  for (const node of nodeByCode.values()) {
    const parent = parentCode(node.code);
    if (parent) {
      const parentNode = nodeByCode.get(parent);
      if (parentNode) parentNode.children.push(node);
      else roots.push(node); // shouldn't happen given the synthesis above
    } else {
      roots.push(node);
    }
  }

  const sortNodes = (nodes: FolderNode[]) => {
    nodes.sort((a, b) => compareCodes(a.code, b.code));
    for (const n of nodes) sortNodes(n.children);
  };
  sortNodes(roots);

  return roots;
}

function compareCodes(a: string, b: string): number {
  const as = a.split(".");
  const bs = b.split(".");
  for (let i = 0; i < Math.min(as.length, bs.length); i++) {
    const na = Number(as[i]);
    const nb = Number(bs[i]);
    if (!Number.isNaN(na) && !Number.isNaN(nb) && na !== nb) return na - nb;
    if (as[i] !== bs[i]) return as[i].localeCompare(bs[i]);
  }
  return as.length - bs.length;
}

/* ───────────────────────── Path → code resolver ───────────────────── */

/**
 * Best-effort mapping from a Folder-Mirroring relative path (e.g.,
 * "Financials/Q4 2024") to the deepest matching code in `indexRows`.
 *
 * Strategy: walk down the tree, matching each path segment to a child
 * whose normalized title contains the normalized segment (or vice versa).
 * Returns the deepest matched code; null if nothing matches at the top.
 *
 * If `pathHint` happens to be populated on indexRows, we prefer an exact
 * pathHint match — it's the parser's authoritative folder chain.
 */
export function pathToCode(
  folderPath: string,
  indexRows: IndexRow[],
): string | null {
  if (!folderPath) return null;

  // 1. Exact pathHint match wins when available.
  const lcPath = folderPath.toLowerCase();
  for (const row of indexRows) {
    if (row.pathHint && row.pathHint.toLowerCase() === lcPath) return row.code;
  }

  // 2. Walk the tree segment by segment.
  const segments = folderPath.split("/").map((s) => s.trim()).filter(Boolean);
  if (segments.length === 0) return null;

  const tree = buildFolderTree(indexRows);
  let nodes: FolderNode[] = tree;
  let lastCode: string | null = null;

  for (const segment of segments) {
    const norm = normalizeSegment(segment);
    const match = nodes.find((n) => segmentMatchesTitle(norm, n.title));
    if (!match) break; // path diverges; return what we have
    lastCode = match.code;
    nodes = match.children;
  }

  return lastCode;
}

function normalizeSegment(s: string): string {
  // Strip leading code-ish prefixes ("01 ", "1. ", "01_") and lowercase.
  return s
    .toLowerCase()
    .replace(/^[\d.\s_\-]+/, "")
    .trim();
}

function segmentMatchesTitle(normSegment: string, title: string): boolean {
  if (!normSegment) return false;
  const normTitle = normalizeSegment(title);
  if (!normTitle) return false;
  if (normTitle === normSegment) return true;
  // Loose: one contains the other. Helps when local folder name has extra
  // tokens (e.g., "Financials 2024" vs index "Financials").
  return normTitle.includes(normSegment) || normSegment.includes(normTitle);
}

/* ─────────────────────── Tree exclusion summary ───────────────────── */

export type NodeExclusion = "included" | "excluded" | "partial";

/**
 * Determine a node's tristate from the excluded set:
 *   - excluded: this code or any ancestor is excluded
 *   - partial: at least one descendant is excluded but the node itself isn't
 *   - included: nothing in the subtree is excluded
 */
export function nodeExclusion(
  node: FolderNode,
  excluded: Set<string>,
): NodeExclusion {
  if (isCodeExcluded(node.code, excluded)) return "excluded";
  if (hasExcludedDescendant(node, excluded)) return "partial";
  return "included";
}

function hasExcludedDescendant(node: FolderNode, excluded: Set<string>): boolean {
  for (const child of node.children) {
    if (excluded.has(child.code)) return true;
    if (hasExcludedDescendant(child, excluded)) return true;
  }
  return false;
}

/**
 * Collect every code in the subtree rooted at `node` (including the node).
 */
export function collectSubtreeCodes(node: FolderNode): string[] {
  const out = [node.code];
  for (const child of node.children) {
    out.push(...collectSubtreeCodes(child));
  }
  return out;
}
